import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type BankAccountResponse,
  formatMoney,
  fromCents,
  LOAN_KIND_LABELS,
  LOAN_KINDS,
  type LoanKind,
  type LoanRequest,
  type LoanResponse,
  loanSchema,
  type LoanSummary,
  toCents,
} from '@k-management/shared';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { type FieldErrors, showServerError, validate } from '../../lib/form-validation.js';
import { moneyOf } from '../people/people-ui.js';
import { SubjectPicker } from './SubjectPayments.js';

export const loanKeys = {
  all: ['treasury', 'loans'] as const,
  one: (id: string) => ['treasury', 'loans', id] as const,
};

/** How much of the loan is repaid, for the bar of the card. */
function repaid(loan: LoanSummary): number | null {
  const lent = loan.amount !== null ? toCents(loan.amount) : null;
  if (!lent || loan.residual === null) return null;
  return Math.min(100, Math.max(0, Math.round(100 - (toCents(loan.residual) / lent) * 100)));
}

/**
 * Tesoreria › Mutui e finanziamenti (09/10/2026): a card for each loan with
 * the debt left, and the totals of the debts and of the next twelve months.
 */
export function LoansPage() {
  usePageTitle('Mutui e finanziamenti');
  const canWrite = useCan('tesoreria.write');
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const loans = useQuery({
    queryKey: loanKeys.all,
    queryFn: () => apiRequest<LoanSummary[]>('/treasury/loans'),
  });
  useCommands(
    canWrite
      ? [
          {
            key: 'new',
            label: 'Nuovo mutuo o finanziamento',
            icon: 'plus',
            primary: true,
            onClick: () => setCreating(true),
          } satisfies Command,
        ]
      : [],
  );
  const all = loans.data ?? [];
  const open = all.filter(
    (loan) => loan.active && (loan.residual === null || toCents(loan.residual) !== 0),
  );
  const shown = showClosed ? all : open;
  const sum = (pick: (loan: LoanSummary) => string | null) =>
    fromCents(all.reduce((total, loan) => total + toCents(pick(loan) ?? '0'), 0));

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Mutui e finanziamenti</h1>
          <p className="muted">
            Le rate arrivano dai movimenti delle banche, riconosciute dal numero del mutuo con
            capitale, interessi e spese; i movimenti senza numero (un prestito socio) si abbinano
            dalla riconciliazione. Il debito residuo parte da quello noto a una data.
          </p>
        </div>
      </div>
      {loans.isError && <p className="error">{loans.error.message}</p>}
      {loans.data && (
        <>
          <div className="treasury-tiles">
            <div className="treasury-tile">
              <span>Debito residuo</span>
              <strong>{formatMoney(sum((loan) => loan.residual))}</strong>
            </div>
            <div className="treasury-tile">
              <span>Rate dei prossimi 12 mesi (stima)</span>
              <strong>{formatMoney(sum((loan) => loan.next12Months))}</strong>
            </div>
            <div className="treasury-tile">
              <span>Interessi pagati quest’anno</span>
              <strong>{formatMoney(sum((loan) => loan.interestThisYear))}</strong>
            </div>
          </div>
          {all.length > open.length && (
            <label className="check">
              <input
                type="checkbox"
                checked={showClosed}
                onChange={(event) => setShowClosed(event.target.checked)}
              />
              Anche estinti e disattivati ({all.length - open.length})
            </label>
          )}
          {all.length === 0 && (
            <p className="notice info">
              Nessun mutuo inserito
              {canWrite ? ': inseriscili con «Nuovo mutuo o finanziamento».' : '.'}
            </p>
          )}
          <div className="loan-cards">
            {shown.map((loan) => {
              const bar = repaid(loan);
              return (
                <Link key={loan.id} to={`/tesoreria/mutui/${loan.id}`} className="card loan-card">
                  <strong>{loan.name}</strong>
                  <small className="muted block">
                    {[LOAN_KIND_LABELS[loan.kind], loan.lender, loan.number]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                  {bar !== null && (
                    <div className="loan-bar" title={`Rimborsato ${bar}%`}>
                      <i style={{ width: `${bar}%` }} />
                    </div>
                  )}
                  <span className="loan-residual">
                    {loan.residual === null ? '—' : formatMoney(loan.residual)}{' '}
                    <small className="muted">debito residuo</small>
                  </span>
                  <small className="muted block">
                    {loan.residual !== null && toCents(loan.residual) === 0
                      ? 'estinto'
                      : loan.nextInstalment
                        ? `prossima rata ${formatDate(loan.nextInstalment.date)}: ${loan.nextInstalment.estimated ? '~' : ''}${formatMoney(loan.nextInstalment.amount)}`
                        : `${loan.paidInstalments} rate trovate nei movimenti`}
                  </small>
                </Link>
              );
            })}
          </div>
        </>
      )}
      {creating && (
        <LoanDialog
          onClose={() => setCreating(false)}
          onSaved={(saved) => navigate(`/tesoreria/mutui/${saved.id}`)}
        />
      )}
    </section>
  );
}

const EMPTY: LoanRequest = {
  kind: 'mutuo',
  name: '',
  accountCode: '',
  lender: null,
  number: null,
  amount: null,
  startDate: null,
  endDate: null,
  instalments: null,
  anchorDate: null,
  anchorResidual: null,
  rate: null,
  notes: null,
  active: true,
};

/** New loan, or changes to one: the data the debt left is counted from. */
export function LoanDialog({
  loan,
  onClose,
  onSaved,
}: {
  loan?: LoanSummary;
  onClose: () => void;
  onSaved?: (saved: LoanResponse) => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [draft, setDraft] = useState<LoanRequest>(
    loan
      ? {
          kind: loan.kind,
          name: loan.name,
          accountCode: loan.accountCode,
          lender: loan.lender,
          number: loan.number,
          amount: loan.amount,
          startDate: loan.startDate,
          endDate: loan.endDate,
          instalments: loan.instalments,
          anchorDate: loan.anchorDate,
          anchorResidual: loan.anchorResidual,
          rate: loan.rate,
          notes: loan.notes,
          active: loan.active,
          subjectId: loan.subjectId,
        }
      : EMPTY,
  );
  // The subject of a partner's loan (2.22), shown in his account statement.
  const [subject, setSubject] = useState<{ id: string; name: string } | null>(
    loan?.subjectId && loan.subjectName ? { id: loan.subjectId, name: loan.subjectName } : null,
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const banks = useQuery({
    queryKey: ['treasury', 'banks'],
    queryFn: () => apiRequest<BankAccountResponse[]>('/treasury/banks'),
  });
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const set = <K extends keyof LoanRequest>(key: K, value: LoanRequest[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const text = (value: string) => value.trim() || null;
  const save = useMutation({
    mutationFn: (body: LoanRequest) =>
      apiRequest<LoanResponse>(loan ? `/treasury/loans/${loan.id}` : '/treasury/loans', {
        method: loan ? 'PATCH' : 'POST',
        body,
      }),
    onSuccess: async (saved) => {
      queryClient.setQueryData(loanKeys.one(saved.id), saved);
      await queryClient.invalidateQueries({ queryKey: ['treasury'] });
      onSaved?.(saved);
      onClose();
    },
    onError: (error) => showServerError(error, setErrors, setFormError),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    // The amounts as the API wants them («1850.00»), whatever was typed.
    const body: LoanRequest = {
      ...draft,
      subjectId: subject?.id ?? null,
      amount: draft.amount ? (moneyOf(draft.amount) ?? draft.amount) : null,
      anchorResidual: draft.anchorResidual
        ? (moneyOf(draft.anchorResidual) ?? draft.anchorResidual)
        : null,
    };
    const result = validate(loanSchema, body);
    setErrors(result.errors);
    if (Object.keys(result.errors).length === 0) save.mutate(body);
    else setFormError(Object.values(result.errors)[0] ?? 'Controlla i dati');
  };

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <form className="stack" onSubmit={submit} noValidate>
        <h2>{loan ? 'Modifica' : 'Nuovo mutuo o finanziamento'}</h2>
        <div className="form-grid">
          <Field label="Tipo">
            <select value={draft.kind} onChange={(e) => set('kind', e.target.value as LoanKind)}>
              {LOAN_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {LOAN_KIND_LABELS[kind]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Nome" error={errors.name}>
            <input
              value={draft.name}
              placeholder="Es. Mutuo Intesa sede"
              onChange={(e) => set('name', e.target.value)}
            />
          </Field>
          <Field label="Conto di addebito" error={errors.accountCode}>
            <select value={draft.accountCode} onChange={(e) => set('accountCode', e.target.value)}>
              <option value="">— scegli —</option>
              {(banks.data ?? []).map((bank) => (
                <option key={bank.code} value={bank.code}>
                  {bank.alias}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Banca o socio">
            <input
              value={draft.lender ?? ''}
              onChange={(e) => set('lender', text(e.target.value))}
            />
          </Field>
          <Field
            label="Anagrafica"
            hint="Per un prestito socio: compare nella sezione finanziaria della sua scheda contabile."
          >
            <SubjectPicker value={subject} onChange={setSubject} />
          </Field>
          <Field
            label="Numero del mutuo"
            error={errors.number}
            hint="Come lo scrive la banca (es. 08/12345678 o 021/123456): le rate si riconoscono da questo."
          >
            <input
              className="mono-input"
              value={draft.number ?? ''}
              onChange={(e) => set('number', text(e.target.value))}
            />
          </Field>
          <Field label="Importo erogato" error={errors.amount}>
            <DecimalInput
              value={draft.amount ?? ''}
              onChange={(value) => set('amount', value || null)}
              decimals={[2, 2]}
            />
          </Field>
          <Field label="Data di erogazione">
            <input
              type="date"
              value={draft.startDate ?? ''}
              onChange={(e) => set('startDate', e.target.value || null)}
            />
          </Field>
          <Field label="Ultima scadenza">
            <input
              type="date"
              value={draft.endDate ?? ''}
              onChange={(e) => set('endDate', e.target.value || null)}
            />
          </Field>
          <Field label="Numero di rate" error={errors.instalments}>
            <input
              type="number"
              min={1}
              value={draft.instalments ?? ''}
              onChange={(e) => set('instalments', e.target.value ? Number(e.target.value) : null)}
            />
          </Field>
          <Field label="Tasso" hint="Solo da leggere, es. «variabile, 5,955%».">
            <input value={draft.rate ?? ''} onChange={(e) => set('rate', text(e.target.value))} />
          </Field>
        </div>
        <fieldset className="stack">
          <legend>Debito residuo noto</legend>
          <p className="muted small">
            Il residuo che la banca indica a una data (es. «Affidamenti» di Intesa): da lì si
            contano le rate prima e dopo. Senza, si parte dall’importo erogato alla data di
            erogazione.
          </p>
          <div className="form-grid">
            <Field label="Alla data">
              <input
                type="date"
                value={draft.anchorDate ?? ''}
                onChange={(e) => set('anchorDate', e.target.value || null)}
              />
            </Field>
            <Field label="Debito residuo" error={errors.anchorResidual}>
              <DecimalInput
                value={draft.anchorResidual ?? ''}
                onChange={(value) => set('anchorResidual', value || null)}
                decimals={[2, 2]}
              />
            </Field>
          </div>
        </fieldset>
        <Field label="Note">
          <textarea
            value={draft.notes ?? ''}
            rows={2}
            onChange={(e) => set('notes', text(e.target.value))}
          />
        </Field>
        {loan && (
          <label className="check">
            <input
              type="checkbox"
              checked={draft.active ?? true}
              onChange={(e) => set('active', e.target.checked)}
            />
            Attivo (si propone nella riconciliazione)
          </label>
        )}
        {formError && <p className="error">{formError}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button type="submit" className="primary" disabled={save.isPending}>
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </form>
    </dialog>
  );
}
